import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const WALLET_STORAGE_KEY = 'bromentum_wallet_address';
const CHAIN_STORAGE_KEY = 'bromentum_chain_type';

export const useWalletPersistence = () => {
  const [persistedWallet, setPersistedWallet] = useState<string | null>(null);
  const [persistedChainType, setPersistedChainType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load persisted wallet data on mount
  useEffect(() => {
    const loadPersistedData = () => {
      try {
        const wallet = localStorage.getItem(WALLET_STORAGE_KEY);
        const chainType = localStorage.getItem(CHAIN_STORAGE_KEY);
        
        setPersistedWallet(wallet);
        setPersistedChainType(chainType || 'solana');
      } catch (error) {
        console.error('Error loading persisted wallet data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPersistedData();
  }, []);

  // Persist wallet data
  const persistWallet = async (walletAddress: string, chainType: string = 'solana') => {
    try {
      // Store in localStorage for immediate persistence using multiple keys for compatibility
      localStorage.setItem(WALLET_STORAGE_KEY, walletAddress);
      localStorage.setItem(CHAIN_STORAGE_KEY, chainType);
      
      // Also store in standardized keys for unified authentication
      localStorage.setItem('connected_wallet_address', walletAddress);
      localStorage.setItem('connected_wallet_chain', chainType);
      localStorage.setItem('walletAddress', walletAddress); // Legacy compatibility
      
      setPersistedWallet(walletAddress);
      setPersistedChainType(chainType);
      
      console.log('Wallet persisted to multiple storage keys:', { walletAddress, chainType });
      return true;
    } catch (error) {
      console.error('Error persisting wallet:', error);
      return false;
    }
  };

  // Clear persisted data
  const clearPersistedWallet = () => {
    try {
      localStorage.removeItem(WALLET_STORAGE_KEY);
      localStorage.removeItem(CHAIN_STORAGE_KEY);
      
      // Also clear standardized keys
      localStorage.removeItem('connected_wallet_address');
      localStorage.removeItem('connected_wallet_chain');
      localStorage.removeItem('walletAddress');
      
      setPersistedWallet(null);
      setPersistedChainType(null);
    } catch (error) {
      console.error('Error clearing persisted wallet:', error);
    }
  };

  // Check if user is already initiated with this wallet
  const checkWalletInitiation = async (walletAddress: string) => {
    try {
      const { data, error } = await supabase
        .from('initiated_bros')
        .select('id, brocode, rank, bro_score')
        .eq('wallet_address', walletAddress.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking wallet initiation:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error checking wallet initiation:', error);
      return null;
    }
  };

  return {
    persistedWallet,
    persistedChainType,
    isLoading,
    persistWallet,
    clearPersistedWallet,
    checkWalletInitiation
  };
};